import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';

export interface NavItem {
  label: string;
  path: string;
  icon?: React.ComponentType<{ className?: string }>;
  children?: NavItem[];
}

interface SidebarNavProps {
  items: NavItem[];
  isCollapsed: boolean;
  onNavigate: (path: string) => void;
  activePath: string;
  storageKey?: string;
  useCustomMenu?: boolean;
  customMenuVars?: React.CSSProperties; // CSS variables for custom menu colors
}

const SidebarNav: React.FC<SidebarNavProps> = ({ items, isCollapsed, onNavigate, activePath, storageKey, useCustomMenu, customMenuVars }) => {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  React.useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) setOpenGroups(JSON.parse(raw));
    } catch {}
  }, [storageKey]);

  React.useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(openGroups));
    } catch {}
  }, [openGroups, storageKey]);

  const toggleGroup = (label: string) => {
    setOpenGroups(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const menuVariants = [
    'menu-variant-maroon',
    'menu-variant-primary',
    'menu-variant-secondary',
    'menu-variant-success',
    'menu-variant-warning',
    'menu-variant-error',
    'menu-variant-info',
  ];

  const renderItem = (item: NavItem, index: number) => {
    const Icon = item.icon;
    const isActive = activePath === item.path;
    const hasChildren = (item.children || []).length > 0;
    const isOpen = !!openGroups[item.label];
    const variantClass = useCustomMenu ? 'menu-variant-custom' : menuVariants[index % menuVariants.length];
    return (
      <div key={item.label} className="menu-slide-in">
        <button
          onClick={() => hasChildren ? toggleGroup(item.label) : onNavigate(item.path)}
          className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-3 py-2.5 text-base font-medium rounded-lg transition-all duration-300 backdrop-blur-md glass-menu-item ${variantClass} ${isActive ? 'glass-menu-active' : ''}`}
          title={isCollapsed ? item.label : undefined}
        >
          <span className={`flex items-center ${isCollapsed ? '' : 'gap-3'}`}>
            {Icon && <Icon className={`w-5 h-5 ${isCollapsed ? '' : ''}`} />}
            {!isCollapsed && <span>{item.label}</span>}
          </span>
          {hasChildren && !isCollapsed && (
            <ChevronRight className={`w-4 h-4 caret-rotate ${isOpen ? 'rotate-90' : ''}`} />
          )}
        </button>
        {hasChildren && !isCollapsed && (
          <div className={`pl-2 overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 mt-1' : 'max-h-0'}`}>
            {(item.children || []).map(child => (
              <button
                key={child.label}
                onClick={() => onNavigate(child.path)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-base rounded-lg mt-1 glass-menu-item ${variantClass} ${activePath === child.path ? 'glass-menu-active' : ''}`}
                title={child.label}
              >
                {child.icon && <child.icon className="w-4 h-4" />}
                <span>{child.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="glass-menu p-2 space-y-1" style={customMenuVars}>
      {items.map((it, idx) => renderItem(it, idx))}
    </div>
  );
};

export default SidebarNav;


